import React, { useEffect, useState, useContext, useRef } from "react";
import { formatDateOnly } from "helpers/dateOnly";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "components/toast";
import { getQuoteRequest } from "api/QuoteRequests";
import { getRfqMaintenanceContext } from "api/QuoteRequests";
import { trackActivity } from "api/ActivityTracker";
import { declineRfq, reviveRfq, extendRfq } from "api/QuoteExpiration";
import { BackArrowIcon, PencilIcon, QuestionMarkIcon, TrashIcon } from "components/svg";
import H2 from "components/typography/H2";
import H5 from "components/typography/H5";
import Header from "components/ui/Header";
import P from "components/typography/P";
import Page from "components/ui/Page";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge } from "theme-ui";
import { themevals } from "theme/themevals";
import moment from "moment-timezone";
import { ItemsIcon, NotesIcon } from "components/svg";
import Items from "./components/Items";
import Notes from "./components/Notes";
import UserContext from "context/User";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import DeclineReasonModal from "components/DeclineReasonModal";
import QuoteVersionHistory from "components/QuoteVersionHistory";
import SiteMapSection from "components/SiteMapSection";
import MaintenanceContextBanner from "components/MaintenanceContextBanner";

const QuoteRequest = () => {
  const toast = useToast();
  const params = useParams();
  const navigate = useNavigate();

  const [rfq, setRFQ] = useState<any>(null);
  const [maintCtx, setMaintCtx] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("items");
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Modal state
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showReviveModal, setShowReviveModal] = useState(false);

  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  const initRFQ = async () => {
    const res = await getQuoteRequest({ data: { id: params.id } });
    setRFQ(res?.id ? res : null);

    // RFQ pricing-verification pivot: resolve maintenance context (plan,
    // WO, NTE) when this RFQ came from a Work Order. Returns null for a
    // plain RFQ, so the banner + NTE delta only render for WO-sourced
    // maintenance RFQs. Staff-only endpoint; clients get null.
    if (res?.id && user?.role !== null) {
      const ctx = await getRfqMaintenanceContext({ rfqId: res.id });
      setMaintCtx(ctx && ctx.workOrderId ? ctx : null);
    } else {
      setMaintCtx(null);
    }

    if (res?.id && !hasTrackedView.current) {
      hasTrackedView.current = true;
      void trackActivity({
        module: "quote-requests",
        action: "view_quote_request_detail",
        actionType: "view",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote-request",
        entityId: res.id,
        metadata: {},
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    }
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | RFQ";
    hasTrackedView.current = false;
    initRFQ();
  }, [params.id]);

  const handleDecline = async (data: { category: string | null; notes?: string; competitorName?: string }) => {
    if (!rfq?.id) return;
    setSubmitting(true);
    const res = await declineRfq(rfq.id, data);
    if (res.success) {
      toast.add("RFQ cancelled.", "var(--theme-ui-colors-success)");
      setShowDeclineModal(false);
      initRFQ();
    } else {
      toast.add(res.message || "Something went wrong.", "var(--theme-ui-colors-danger)");
    }
    setSubmitting(false);
  };

  const handleRevive = async () => {
    if (!rfq?.id) return;
    setSubmitting(true);
    const res = await reviveRfq(rfq.id);
    if (res.success) {
      toast.add("RFQ reopened.", "var(--theme-ui-colors-success)");
      setShowReviveModal(false);
      initRFQ();
    } else {
      toast.add(res.message || "Something went wrong.", "var(--theme-ui-colors-danger)");
    }
    setSubmitting(false);
  };

  const handleExtend = async () => {
    if (!rfq?.id) return;
    setSubmitting(true);
    const res = await extendRfq(rfq.id);
    if (res.success) {
      const extMsg = "Expiration extended" +
        (res.newExpiresAt ? " to " + moment(res.newExpiresAt).format("MMM D, YYYY") : "") +
        (res.extensionCount && res.maxExtensions ? " (" + res.extensionCount + " of " + res.maxExtensions + " extensions used)" : "");
      toast.add(extMsg, "var(--theme-ui-colors-success)");
      initRFQ();
    } else {
      toast.add(res.message || "Something went wrong.", "var(--theme-ui-colors-danger)");
    }
    setSubmitting(false);
  };

  // Status helpers
  const isActive = rfq?.status === 0 || rfq?.status === 1;
  const isExpired = rfq?.status === 6;
  const isQuoted = rfq?.status === 2;
  // Matches the backend: Accepted (3), Closed (4), Canceled (5) and Expired (6)
  // RFQs reject updates; Quoted (2) is edited through the quote.
  const canEdit = rfq?.status === 0 || rfq?.status === 1;
  const hasExpiration = rfq?.expiresAt && isActive;

  return (
    <>
      <Header></Header>
      {!rfq && (
        <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
          <Spinner size={25} strokeWidth={3} title="loading" />
        </Box>
      )}
      {rfq && (
        <>
          <Page
            backAction={
              <Link variant="back" href="/quote-requests">
                <BackArrowIcon />
                {"Back to RFQ's"}
              </Link>
            }
            $noPadding={true}
          >
            <Flex sx={{ gap: "20px", alignItems: "center" }}>
              <H2>{"RFQ# GMR-" + String(rfq.sequence).padStart(5, "0") + " - " + (rfq.projectName ? rfq.projectName : "Instant Estimate")}</H2>
              {hasExpiration && (
                <BasicButton
                  scheme="secondary"
                  outline={true}
                  styles={{ margin: "0", padding: "4px 12px", fontSize: "12px" }}
                  type="button"
                  onClick={handleExtend}
                  $submitting={submitting}
                  keepText={true}
                >
                  Extend Expiration
                </BasicButton>
              )}
            </Flex>
            <Grid gap={0} columns={[1, "250px 1fr"]}>
              <Box>
                <H5 marginTop={"30px"} marginBottom={"10px"} fontFamily={"Poppins"} color={themevals.colors.dkgrey} fontSize={"14px"} fontWeight={500}>
                  RFQ Details
                </H5>
              </Box>
              <Flex sx={{ paddingLeft: 30, alignItems: "flex-end" }}>
                <Flex sx={{ gap: "40px" }}>
                  <Flex sx={{ paddingBottom: "10px", borderBottom: activeTab === "items" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }} onClick={() => setActiveTab("items")}>
                    <ItemsIcon fill={activeTab === "items" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab === "items" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Items
                    </Box>
                  </Flex>
                  <Flex sx={{ paddingBottom: "10px", borderBottom: activeTab === "notes" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }} onClick={() => setActiveTab("notes")}>
                    <NotesIcon fill={activeTab === "notes" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab === "notes" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Notes
                    </Box>
                  </Flex>
                </Flex>
              </Flex>
            </Grid>
            <Grid gap={0} columns={[1, "250px 1fr"]}>
              {/* See Projects/view.tsx: minWidth 0 + overflowWrap on the
                  sidebar column so emails and URLs wrap inside 250px. */}
              <Box sx={{ borderTop: "1px solid", borderRight: "1px solid", borderColor: "inputOutline", minWidth: 0, overflowWrap: "anywhere" }}>
                {user.role !== null && canEdit && (
                  <Box sx={{ paddingRight: "30px", paddingTop: "20px" }}>
                    <Link
                      href={"/quote-requests/" + rfq.id + "/edit"}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                        width: "100%", padding: "10px 0",
                        border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px",
                        textDecoration: "none", color: themevals.colors.black, backgroundColor: "#fff",
                        fontFamily: "Poppins", fontSize: "13px", fontWeight: 500, cursor: "pointer",
                        "&:hover": { backgroundColor: "#F7F8FA", borderColor: themevals.colors.dkgrey },
                      }}
                    >
                      <PencilIcon height="13px" fill="currentColor" />
                      Edit
                    </Link>
                  </Box>
                )}
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Basic Info
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Project Name</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {rfq.projectName ? rfq.projectName : "Instant Estimate"}
                    </P>
                  </Flex>
                  {user.role !== null && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Company</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {rfq.client?.name}
                      </P>
                    </Flex>
                  )}
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Status</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {(() => {
                        switch (rfq.status) {
                          case 1: return <Badge as="span" variant="warning">New</Badge>;
                          case 2: return <Badge as="span" variant="success">Quoted</Badge>;
                          case 5: return <Badge as="span" variant="danger">Cancelled</Badge>;
                          case 6: return <Badge as="span" variant="orange">Expired</Badge>;
                          default: return <Badge as="span" variant="info">Estimated</Badge>;
                        }
                      })()}
                    </P>
                  </Flex>
                  {(rfq.expiresAt || rfq.expiredAt) && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
                        {rfq.expiredAt ? "Expired On" : "Expires On"}
                      </Label>
                      <P fontFamily={"Poppins"} color={rfq.expiredAt ? themevals.colors.danger : "#FF7F07"} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {moment(rfq.expiredAt || rfq.expiresAt).format("MMM D, YYYY")}
                      </P>
                    </Flex>
                  )}
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Date Added</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {moment(rfq.createdAt).format("MMM D, YYYY")}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Estimated Service Date</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {rfq.serviceDate ? formatDateOnly(rfq.serviceDate) : "-"}
                    </P>
                  </Flex>
                  {/* Install-only: mulch supply preference (pickup vs delivery).
                      Structured operational field, only present on install-only
                      jobs (null everywhere else). The "material supplied by X"
                      wording is NOT hardcoded here - it rides on the install-only
                      service's description, which renders in the line-item table. */}
                  {rfq.mulchSupplyPreference && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Mulch Supply</Label>
                      <Box sx={{ marginTop: "5px" }}>
                        <Badge as="span" variant="info">
                          {rfq.mulchSupplyPreference === "pickup" ? "Pickup" : "Delivery"}
                        </Badge>
                      </Box>
                    </Flex>
                  )}
                </Box>

                {/* Version History — show all quotes under this RFQ */}
                <Box sx={{ px: 0, pb: 2, pr: "30px" }}>
                  <QuoteVersionHistory quoteRequestId={rfq.id} />
                </Box>

                <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                {rfq.primaryClient && (
                  <>
                    <Box sx={{ paddingRight: "30px" }}>
                      <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                        {user.role !== null ? "Primary Client" : "Primary Contact"}
                      </H5>
                      <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                        <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Name</Label>
                        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                          {rfq.primaryClient.name}
                        </P>
                      </Flex>
                      <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                        <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Email</Label>
                        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                          {rfq.primaryClient.contactEmail}
                        </P>
                      </Flex>
                      {rfq.primaryClient.phone && (
                        <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                          <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Phone</Label>
                          <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                            {rfq.primaryClient.phone}
                          </P>
                        </Flex>
                      )}
                    </Box>
                    <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                  </>
                )}
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Address
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {rfq.location.address.line1 && rfq.location.address.line1 !== rfq.location.address.city
                        ? rfq.location.address.line1 + (rfq.location.address.line2 ? ", " + rfq.location.address.line2 : "")
                        : rfq.location.address.line2
                          ? rfq.location.address.line2
                          : ""}
                    </P>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {rfq.location.address.city + ", " + rfq.location.address.state}
                      {rfq.location.address.postalCode !== "null" ? " " + rfq.location.address.postalCode : ""}
                    </P>
                  </Flex>
                </Box>

                {rfq.location?.id && user.role !== null && (
                  <>
                    {/*
                     * Session 30: only admin users see the map section on
                     * RFQs. Clients get nothing here, matching the
                     * Quote/Invoice/PO pattern. Snapshots are not attached
                     * to RFQs — clients will see them on the resulting
                     * Quote when one is generated.
                     */}
                    <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                    <SiteMapSection
                      locationId={rfq.location.id}
                      mulchMapId={rfq.mulchMapId ?? null}
                      recordType="quote_request"
                      recordId={rfq.id}
                      onAttachChanged={() => initRFQ()}
                    />
                  </>
                )}

                {/* Sidebar actions — danger zone only (revive moved to content banner) */}
                {(isActive || isQuoted) && (
                  <Box sx={{ paddingBottom: "60px", paddingRight: "30px" }}>
                    <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                    <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                      Danger Zone
                    </H5>
                    <Flex sx={{ flexDirection: "column", gap: "8px" }}>
                      <BasicButton
                        scheme="dangerSecondary"
                        styles={{ margin: "0" }}
                        type="button"
                        onClick={() => setShowDeclineModal(true)}
                        $iconHeight="14px"
                        $iconWidth="14px"
                        keepText={true}
                      >
                        <TrashIcon fill="#A60000" />
                        Cancel RFQ
                      </BasicButton>
                    </Flex>
                  </Box>
                )}
              </Box>
              {/* Interior content */}
              <Box sx={{ paddingTop: "30px", paddingLeft: "30px", borderTop: "1px solid", borderColor: "inputOutline" }}>
                {/* Maintenance context banner (RFQ pricing-verification
                    pivot) — only for WO-sourced RFQs. A15: this is now the
                    shared `MaintenanceContextBanner`, the same component
                    the quote/invoice/PO use, so the "back to work order"
                    affordance reads identically everywhere. It
                    self-fetches the context from the RFQ id; the local
                    `maintCtx` fetch above is kept only because the Items
                    panel needs `maintCtx.nteAmount` for the live NTE
                    delta. */}
                {maintCtx && <MaintenanceContextBanner rfqId={rfq.id} />}
                {/* Expired banner — top of content area */}
                {isExpired && (
                  <Flex sx={{
                    backgroundColor: "#FFF8F0",
                    border: "1px solid #FF7F07",
                    borderRadius: "8px",
                    padding: "16px 20px",
                    marginBottom: "24px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "16px",
                  }}>
                    <Box>
                      <P fontFamily="Poppins" color="#000" fontSize="14px" fontWeight={600} marginTop="0px" marginBottom="4px">
                        This RFQ expired on {moment(rfq.expiredAt || rfq.expiresAt).format("MMM D, YYYY")}
                      </P>
                      <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="12px" fontWeight={400} marginTop="0px" marginBottom="0px">
                        Reopen this RFQ to refresh pricing and generate a new quote.
                      </P>
                    </Box>
                    <Flex sx={{ gap: "8px", flexShrink: 0 }}>
                      <BasicButton
                        scheme="primary"
                        styles={{ margin: "0" }}
                        type="button"
                        onClick={() => setShowReviveModal(true)}
                        keepText={true}
                      >
                        Reopen RFQ
                      </BasicButton>
                    </Flex>
                  </Flex>
                )}
                {activeTab === "items" && <Items rfq={rfq} isCompany={user.role !== null ? true : false} nteAmount={maintCtx?.nteAmount ?? null} onChanged={() => initRFQ()} />}
                {activeTab === "notes" && <Notes rfq={rfq} isCompany={user.role !== null ? true : false} />}
              </Box>
            </Grid>
          </Page>

          {/* Decline/Cancel Modal with Reason */}
          {showDeclineModal && (
            <DeclineReasonModal
              type="rfq"
              onConfirm={handleDecline}
              onCancel={() => setShowDeclineModal(false)}
              submitting={submitting}
            />
          )}

          {/* Revive Confirmation Modal */}
          {showReviveModal && (
            <BasicModal
              title="Reopen RFQ?"
              cancel_click={() => setShowReviveModal(false)}
              cancel_text="Cancel"
              size="sm"
              icon={<QuestionMarkIcon fill="#818181" height="30px" />}
              actions={
                <BasicButton
                  type="button"
                  scheme="primary"
                  onClick={handleRevive}
                  $submitting={submitting}
                  $loadingColor="#fff"
                  keepText={true}
                  styles={{ margin: "0 0.625rem .25rem 0" }}
                >
                  Yes, reopen
                </BasicButton>
              }
            >
              <P>This will reopen the RFQ with a fresh expiration window and update estimate pricing to current rates.</P>
            </BasicModal>
          )}
        </>
      )}
    </>
  );
};

export default QuoteRequest;
